"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReaderReportType } from "@/lib/reports/report-type";
import { useExploreRoute } from "../_lib/explore-route-context";
import { getHubUrl, switchExploreEdition } from "../_lib/explore-routes";
import { ExploreThemeToggle } from "./explore-theme-toggle";
import { useNavigationTransition } from "./navigation-transition-provider";
import { SubscribeDialog } from "./subscribe-dialog";

type ReportType = ReaderReportType;

export interface ExploreNavLink {
  label: string;
  suffix: string;
  /**
   * Overrides `suffix` when building the link's href. Lets a nav item own
   * every route under `suffix` for active-state purposes while actually
   * navigating somewhere more specific.
   */
  hrefSuffix?: string;
  /** When true, any path nested under `${exploreBase}${suffix}/` is also
   * considered active, not just an exact match. */
  matchSubroutes?: boolean;
}

export const NAV_SUFFIXES: ExploreNavLink[] = [
  { label: "explore", suffix: "" },
  { label: "all stories", suffix: "/stories" },
  // Tools sits right after Stories: PostHog shows /engineer/tools has the
  // best pv/session of any surface on the site (3.78), but almost nobody
  // finds it because it was hidden two clicks deep in the explore hub.
  // Surfacing it in the desktop nav is the cheapest discoverability win.
  // The nav entry opens the by-category page (the preferred entry point),
  // but still owns the flat catalog and product detail pages for
  // active-state highlighting.
  {
    label: "all tools",
    suffix: "/tools",
    hrefSuffix: "/tools/categories",
    matchSubroutes: true,
  },
  { label: "all skills", suffix: "/skills" },
];

export function getExploreNavHref({
  link,
  exploreBase,
  hubUrl,
}: {
  link: ExploreNavLink;
  exploreBase: string;
  hubUrl: string;
}): string {
  const suffix = link.hrefSuffix ?? link.suffix;
  return suffix ? `${exploreBase}${suffix}` : hubUrl;
}

export function isExploreNavLinkActive({
  link,
  pathname,
  exploreBase,
  hubUrl,
}: {
  link: ExploreNavLink;
  pathname: string;
  exploreBase: string;
  hubUrl: string;
}): boolean {
  // Active detection must handle both short URLs (/, /engineer)
  // and internal paths (/en/creative/explore)
  if (!link.suffix) {
    return (
      pathname === exploreBase ||
      pathname === hubUrl ||
      (pathname.startsWith(`${exploreBase}/`) &&
        !NAV_SUFFIXES.some(
          (item) =>
            item.suffix && pathname.startsWith(`${exploreBase}${item.suffix}`)
        ))
    );
  }

  const activePath = `${exploreBase}${link.suffix}`;
  if (link.matchSubroutes) {
    return pathname === activePath || pathname.startsWith(`${activePath}/`);
  }

  return pathname === activePath;
}

const SLIDE_DURATION_MS = 280;
const GHOST_COUNT = 5;
const GHOST_OPACITY = 0.2;
const GHOST_BLUR = 4;

function AnimatedModeToggle({
  activeMode,
  prevMode,
  isAnimating,
  isCollapsing,
  showTrails,
  targetLeftPct,
  onToggle,
  className,
  size,
}: {
  activeMode: ReportType;
  prevMode: ReportType;
  isAnimating: boolean;
  isCollapsing: boolean;
  showTrails: boolean;
  targetLeftPct: string;
  onToggle: (mode: ReportType) => void;
  className?: string;
  size: "sm" | "lg";
}) {
  const buttonClasses =
    size === "lg"
      ? "px-1.5 py-1 text-[0.68rem] min-[360px]:px-2 min-[360px]:text-[0.75rem] sm:px-3 sm:py-1.5 sm:text-[0.85rem]"
      : "px-2 py-1 text-[0.7rem] sm:px-3 sm:py-1.5 sm:text-[0.8rem]";

  return (
    <div
      className={`relative items-center overflow-hidden rounded-full bg-[var(--explore-bg)] p-[3px] ${className ?? ""}`}
    >
      {/* Ghost trails */}
      {showTrails &&
        Array.from({ length: GHOST_COUNT }, (_, i) => {
          const progress = (i + 1) / (GHOST_COUNT + 1);
          const fromLeft = prevMode === "creative" ? 3 : 50;
          const toLeft = activeMode === "creative" ? 3 : 50;
          const spreadLeft = fromLeft + (toLeft - fromLeft) * progress;
          return (
            <div
              className="pointer-events-none absolute top-[3px] bottom-[3px] z-[5] rounded-full"
              key={`ghost-${i}`}
              style={{
                backgroundColor: "var(--explore-accent-yellow)",
                width: "calc(50% - 3px)",
                left: isCollapsing
                  ? `${targetLeftPct}%`
                  : `${String(spreadLeft)}%`,
                opacity: isCollapsing
                  ? 0
                  : GHOST_OPACITY * (1 - progress * 0.6),
                filter: `blur(${String(isCollapsing ? GHOST_BLUR * 2 : GHOST_BLUR * progress)}px)`,
                transition: isCollapsing
                  ? `left ${String(Math.round(SLIDE_DURATION_MS * 0.3))}ms cubic-bezier(0.23, 1, 0.32, 1), opacity ${String(Math.round(SLIDE_DURATION_MS * 0.25))}ms ease-out, filter ${String(Math.round(SLIDE_DURATION_MS * 0.2))}ms ease`
                  : "none",
              }}
            />
          );
        })}
      {/* Golden streak line */}
      {showTrails && (
        <div
          className="-translate-y-1/2 pointer-events-none absolute top-1/2 z-[4] rounded-full"
          style={{
            height: 3,
            left: "15%",
            right: "15%",
            background: "var(--explore-toggle-trail-gradient)",
            opacity: isCollapsing ? 0 : 1,
            transition: `opacity ${String(Math.round(SLIDE_DURATION_MS * 0.2))}ms ease-out`,
          }}
        />
      )}
      {/* Sliding yellow pill */}
      <div
        className="absolute top-[3px] bottom-[3px] z-[6] rounded-full"
        style={{
          backgroundColor: "var(--explore-accent-yellow)",
          width: "calc(50% - 3px)",
          left: activeMode === "creative" ? "3px" : "calc(50%)",
          boxShadow: isAnimating
            ? "var(--explore-toggle-pill-shadow-active)"
            : "var(--explore-toggle-pill-shadow-idle)",
          transition: `left ${String(SLIDE_DURATION_MS)}ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow ${String(SLIDE_DURATION_MS)}ms ease-out`,
        }}
      />
      {(["creative", "engineer"] as const).map((mode) => (
        <button
          className={`after:-inset-x-1 after:-inset-y-2 relative z-10 cursor-pointer select-none rounded-full border-none bg-transparent font-semibold after:absolute after:content-[''] ${buttonClasses} ${
            activeMode === mode
              ? "text-[var(--explore-on-accent-text)]"
              : "text-[var(--explore-text-muted)] hover:text-[var(--explore-text-secondary)]"
          }`}
          key={mode}
          onClick={() => onToggle(mode)}
          onPointerDown={(e) => {
            e.currentTarget.style.transform = "scale(0.97)";
          }}
          onPointerLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
          onPointerUp={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
          style={{
            transition: `color ${String(Math.round(SLIDE_DURATION_MS * 0.5))}ms ease-out, transform 120ms ease-out`,
          }}
          type="button"
        >
          {mode === "creative" ? "Creative" : "Engineer"}
        </button>
      ))}
    </div>
  );
}

export function ExploreHeader() {
  const pathname = usePathname();
  const { navigateWithTransition } = useNavigationTransition();
  const { reportType: routeMode, exploreBase } = useExploreRoute();
  const [visualMode, setVisualMode] = useState<ReportType>(routeMode);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [prevMode, setPrevMode] = useState<ReportType>(routeMode);
  const navigationTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  const activeMode =
    isAnimating || isCollapsing || visualMode !== routeMode
      ? visualMode
      : routeMode;

  useEffect(() => {
    setVisualMode(routeMode);
  }, [routeMode]);

  const hubUrl = getHubUrl({ reportType: routeMode });
  const targetLeftPct = activeMode === "creative" ? "3" : "50";

  const handleToggle = useCallback(
    (mode: ReportType) => {
      if (mode === visualMode || isAnimating) {
        return;
      }

      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      setPrevMode(visualMode);
      setVisualMode(mode);
      setIsAnimating(true);
      setIsCollapsing(false);

      setTimeout(
        () => {
          setIsAnimating(false);
          setIsCollapsing(true);
        },
        Math.round(SLIDE_DURATION_MS * 0.6)
      );

      navigationTimeoutRef.current = setTimeout(() => {
        setIsCollapsing(false);
        const targetUrl = switchExploreEdition({
          currentPathname: pathname,
          currentReportType: routeMode,
          targetReportType: mode,
        });
        // Edition switch is a large context change (different editorial
        // voice, sidebar, feed, category weighting). The short-URL rewrite
        // pattern (`/engineer/*` → `/en/engineer/explore/*`) confuses
        // `router.push` inside `startTransition` — the RSC payload loads
        // but the browser URL bar never updates. A hard navigation avoids
        // that whole class of transition vs. rewrite bugs and guarantees
        // the destination URL is reflected in the bar, history, and
        // analytics.
        if (typeof window !== "undefined") {
          window.location.href = targetUrl;
          return;
        }
        navigateWithTransition(targetUrl);
      }, SLIDE_DURATION_MS + 50);
    },
    [visualMode, isAnimating, navigateWithTransition, routeMode, pathname]
  );

  const showTrails = isAnimating || isCollapsing;

  const toggleProps = {
    activeMode,
    prevMode,
    isAnimating,
    isCollapsing,
    showTrails,
    targetLeftPct,
    onToggle: handleToggle,
  };

  return (
    <header
      className="sticky top-0 z-50 border-[var(--explore-border-light)] border-b bg-[var(--explore-bg-card)] backdrop-blur-sm"
      style={{
        fontFamily: "var(--homepage-sans-stack)",
        paddingTop: "env(safe-area-inset-top, 0px)",
      }}
    >
      <div className="mx-auto grid h-12 max-w-[1320px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-4 sm:h-16 sm:gap-4 sm:px-6 lg:grid-cols-[1fr_auto_1fr] lg:gap-6">
        {/* Left: Logo + toggle (desktop) */}
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <Link
            className="flex min-w-0 items-center gap-2 no-underline"
            href={hubUrl}
            prefetch={false}
          >
            <Image
              alt="AI Primer"
              className="h-[30px] w-[30px] rounded-full sm:h-[34px] sm:w-[34px]"
              height={34}
              src="/logo.webp"
              width={34}
            />
            <span className="hidden whitespace-nowrap font-bold text-[0.9rem] text-[var(--explore-text-primary)] sm:text-[1.05rem] min-[360px]:inline">
              AI Primer
            </span>
          </Link>
          {/* Toggle — desktop only (next to logo) */}
          <AnimatedModeToggle
            {...toggleProps}
            className="hidden lg:flex"
            size="sm"
          />
        </div>

        {/* Center: toggle (mobile) / nav (desktop) */}
        <div className="flex min-w-0 items-center justify-center gap-1">
          {/* Toggle — mobile only (centered) */}
          <AnimatedModeToggle
            {...toggleProps}
            className="flex lg:hidden"
            size="lg"
          />
          <nav className="hidden items-center gap-1 lg:flex">
            {NAV_SUFFIXES.map((link) => {
              // Use short hub URL for the explore link, long URL for sub-routes
              const href = getExploreNavHref({ link, exploreBase, hubUrl });
              const isActive = isExploreNavLinkActive({
                link,
                pathname,
                exploreBase,
                hubUrl,
              });
              return (
                <Link
                  className={`px-3 py-1.5 font-medium text-[0.9rem] no-underline underline-offset-4 ${
                    isActive
                      ? "text-[var(--explore-text-primary)] hover:underline"
                      : "text-[var(--explore-text-muted)] hover:text-[var(--explore-text-primary)]"
                  }`}
                  href={href}
                  key={link.label}
                  prefetch={false}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Right: Theme toggle (desktop only) + Subscribe */}
        <div className="flex items-center gap-1 justify-self-end max-[359px]:gap-0.5 sm:gap-2">
          <div className="hidden lg:flex">
            <ExploreThemeToggle />
          </div>
          <SubscribeDialog>
            <button
              aria-label="Subscribe"
              className="flex cursor-pointer items-center justify-center rounded-full border-none bg-transparent font-semibold text-[0.8rem] text-[var(--explore-text-primary)] shadow-none transition-transform duration-150 active:scale-95 lg:bg-[var(--explore-accent-yellow)] lg:px-4 lg:py-1.5 lg:text-[0.85rem] lg:text-[var(--explore-on-accent-text)] lg:hover:bg-[var(--explore-accent-yellow)]/90"
              type="button"
            >
              Subscribe
            </button>
          </SubscribeDialog>
        </div>
      </div>
    </header>
  );
}
